import { PttUiState } from '../types/ptt-ui-state';
import type { WebRTCTalkSignal } from '../types/webrtc-events';
import { TALK_CONFIRMATION_TIMEOUT_MS } from './webrtc-config';

/** What talkback needs from a connection: a microphone it can open and close. */
export interface TalkMic {
	startTalk: () => Promise<void>;
	stopTalk: () => Promise<void>;
}

/** The consumer that claimed talkback, and how it hears about an ending it did not choose. */
export interface TalkConsumer {
	onTalkTimeout?: (desired: boolean) => void;
	onTalkFailed?: () => void;
}

export interface TalkController {
	readonly state: PttUiState;
	request: (consumer: TalkConsumer, desired: boolean) => Promise<void>;
	signal: (signal: WebRTCTalkSignal) => void;
	isClaimedBy: (consumer: TalkConsumer) => boolean;
	stop: () => void;
	reset: () => void;
}

interface TalkControllerPorts {
	mic: () => TalkMic | null;
	publish: (state: PttUiState) => void;
	trace: (event: string, detail?: Record<string, unknown>) => void;
}

const isClaimed = (state: PttUiState) => state === PttUiState.GRANTED || state === PttUiState.REQUESTING;

const canRequest = (state: PttUiState, desired: boolean) => {
	if (state === PttUiState.REQUESTING || desired === (state === PttUiState.GRANTED)) {
		return false;
	}
	return desired ? state === PttUiState.READY : true;
};

export const createTalkController = ({ mic, publish, trace }: TalkControllerPorts): TalkController => {
	let state = PttUiState.UNAVAILABLE;
	let consumer: TalkConsumer | null = null;
	let confirmationTimeout: ReturnType<typeof setTimeout> | null = null;

	const setState = (nextState: PttUiState) => {
		if (state !== nextState) {
			trace(`ptt ${state} → ${nextState}`);
		}
		state = nextState;
		publish(state);
	};

	const clearConfirmationTimeout = () => {
		if (confirmationTimeout) {
			clearTimeout(confirmationTimeout);
			confirmationTimeout = null;
		}
	};

	const fail = () => {
		const failedConsumer = consumer;
		consumer = null;
		trace('ptt channel lost mid-claim, releasing the microphone');
		mic()
			?.stopTalk()
			.catch(() => {});
		failedConsumer?.onTalkFailed?.();
	};

	const signal = (talkSignal: WebRTCTalkSignal) => {
		trace(`ptt signal '${talkSignal}'`, { current: state });

		if (talkSignal === 'ready') {
			if (!isClaimed(state)) {
				setState(PttUiState.READY);
			}
			return;
		}

		clearConfirmationTimeout();
		if (talkSignal === 'granted') {
			setState(PttUiState.GRANTED);
			return;
		}

		const wasClaimed = isClaimed(state);
		setState(talkSignal === 'released' ? PttUiState.READY : PttUiState.CLOSED);
		if (talkSignal === 'closed' && wasClaimed) {
			fail();
			return;
		}
		consumer = null;
	};

	const confirmTimeout = (desired: boolean) => {
		const timedOutConsumer = consumer;
		confirmationTimeout = null;
		consumer = null;
		trace(`ptt confirmation timed out (desired=${desired}), forcing state`);
		setState(desired ? PttUiState.GRANTED : PttUiState.READY);
		timedOutConsumer?.onTalkTimeout?.(desired);
	};

	const request = async (requestingConsumer: TalkConsumer, desired: boolean) => {
		const activeMic = mic();
		if (!activeMic || !canRequest(state, desired)) {
			trace(`ptt request desired=${desired} ignored`, {
				hasClient: !!activeMic,
				current: state,
			});
			return;
		}

		trace(`ptt request desired=${desired}`);
		setState(PttUiState.REQUESTING);
		consumer = requestingConsumer;
		clearConfirmationTimeout();

		try {
			await (desired ? activeMic.startTalk() : activeMic.stopTalk());
		} catch (error) {
			trace(`ptt ${desired ? 'startTalk' : 'stopTalk'} threw`, { error: String(error) });
			clearConfirmationTimeout();
			consumer = null;
			setState(PttUiState.READY);
			throw error;
		}

		trace(`ptt ${desired ? 'mic live' : 'mic released'}, awaiting camera confirmation`);
		confirmationTimeout = setTimeout(() => confirmTimeout(desired), TALK_CONFIRMATION_TIMEOUT_MS);
	};

	const stop = () => {
		mic()
			?.stopTalk()
			.catch(() => {});
		if (isClaimed(state)) {
			setState(PttUiState.READY);
		}
		consumer = null;
		clearConfirmationTimeout();
	};

	const reset = () => {
		consumer = null;
		clearConfirmationTimeout();
		setState(PttUiState.UNAVAILABLE);
	};

	return {
		get state() {
			return state;
		},
		request,
		signal,
		isClaimedBy: (candidate) => consumer === candidate,
		stop,
		reset,
	};
};
